import React, { useEffect, useMemo, useState } from 'react';
import { useCaps } from '../../../contexts/CapabilitiesContext';
import { pillActive, pillInactive } from '../styles';
import { useOrderedSubtabs } from '../subtabs';
import { AI_PROMPTS_SUBTAB_CAPS, type AiPromptsSubtabKey } from './types';
import ModelsPanel from './ModelsPanel';
import GeneratorConfigPanel from './GeneratorConfigPanel';

const SUBTABS: { key: AiPromptsSubtabKey; label: string; cap: string }[] = [
  { key: 'models', label: 'Models', cap: AI_PROMPTS_SUBTAB_CAPS.models },
  { key: 'source_of_truth', label: 'Source of Truth', cap: AI_PROMPTS_SUBTAB_CAPS.sourceOfTruth },
  { key: 'jtbd', label: 'Jobs to be Done', cap: AI_PROMPTS_SUBTAB_CAPS.jtbd },
  { key: 'tension', label: 'Tension', cap: AI_PROMPTS_SUBTAB_CAPS.tension },
  { key: 'recipe_attributes', label: 'Recipe Card Attributes', cap: AI_PROMPTS_SUBTAB_CAPS.recipeAttributes },
  { key: 'written_concept', label: 'Written Concept', cap: AI_PROMPTS_SUBTAB_CAPS.writtenConcept },
  { key: 'visual_concept', label: 'Visual Concept', cap: AI_PROMPTS_SUBTAB_CAPS.visualConcept },
];

const AdminAiPromptsTab: React.FC = () => {
  const { can } = useCaps();
  const declared = useMemo(() => SUBTABS, []);
  const visibleSubtabs = useOrderedSubtabs('ai_prompts', declared);
  const [active, setActive] = useState<AiPromptsSubtabKey | null>(null);

  useEffect(() => {
    setActive((current) => {
      if (current && visibleSubtabs.some((t) => t.key === current)) return current;
      return (visibleSubtabs[0]?.key as AiPromptsSubtabKey | undefined) ?? null;
    });
  }, [visibleSubtabs]);

  const fallbackVisible = useMemo(
    () => SUBTABS.filter((tab) => can(tab.cap)),
    [can]
  );
  const pills = visibleSubtabs.length > 0 ? visibleSubtabs : fallbackVisible;
  const activeKey = active ?? (pills[0]?.key as AiPromptsSubtabKey);

  if (pills.length === 0) {
    return (
      <div className="text-sm text-[#8A8A8A]">
        You do not have access to AI prompt configuration.
      </div>
    );
  }

  return (
    <div>
      <div className="flex flex-wrap gap-2 mb-5">
        {pills.map((tab) => (
          <button
            key={tab.key}
            type="button"
            onClick={() => setActive(tab.key as AiPromptsSubtabKey)}
            className={`h-8 px-3 rounded-full text-[13px] font-medium transition-colors ${
              activeKey === tab.key ? pillActive : pillInactive
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>
      {activeKey === 'models' && <ModelsPanel />}
      {activeKey === 'source_of_truth' && (
        <GeneratorConfigPanel
          generatorKey="source_of_truth"
          title="Source of Truth"
          enabled
          defaultVariableKeys={['opportunity_space', 'markets']}
          extraSteps={[{ key: 'market_synthesis', label: 'Market synthesis' }]}
        />
      )}
      {activeKey === 'jtbd' && (
        <GeneratorConfigPanel
          generatorKey="jtbd"
          title="Jobs to be Done"
          enabled
          includeDefaultStep={false}
          defaultVariableKeys={['source_of_truth', 'opportunity_space', 'markets']}
          extraSteps={[
            { key: 'step1', label: 'Step 1: Extract codes' },
            { key: 'step2', label: 'Step 2: Combine buckets' },
            { key: 'step3', label: 'Step 3: Weight buckets' },
            { key: 'step4', label: 'Step 4: Build relationships' },
            { key: 'step5', label: 'Step 5: Transform to JSON' },
            { key: 'step6', label: 'Step 6: Write statements' },
          ]}
        />
      )}
      {activeKey === 'tension' && (
        <GeneratorConfigPanel
          generatorKey="tension"
          title="Tension"
          enabled
          defaultVariableKeys={['source_of_truth', 'jtbd', 'market', 'opportunity_space']}
        />
      )}
      {activeKey === 'recipe_attributes' && (
        <GeneratorConfigPanel
          generatorKey="recipe_attributes"
          title="Recipe Card Attributes"
          enabled
          defaultVariableKeys={[
            'market', 'jtbd', 'tension', 'benefit', 'brand_choice', 'opportunity_space', 'markets',
          ]}
        />
      )}
      {activeKey === 'written_concept' && (
        <GeneratorConfigPanel
          generatorKey="written_concept"
          title="Written Concept"
          enabled
          defaultVariableKeys={[
            'job_to_be_done', 'tension', 'benefit', 'brand_choice', 'format',
            'attributes', 'project_name', 'market', 'opportunity_space',
          ]}
        />
      )}
      {activeKey === 'visual_concept' && (
        <div className="space-y-8">
          <GeneratorConfigPanel
            generatorKey="visual_concept"
            title="Visual Concept"
            enabled
            defaultVariableKeys={[
              'job_to_be_done', 'tension', 'benefit', 'brand_choice', 'format',
              'attributes', 'written_concept', 'project_name', 'market', 'opportunity_space',
            ]}
          />
          <GeneratorConfigPanel
            generatorKey="visual_concept_regenerate"
            title="Visual Concept Regenerate"
            enabled
            defaultVariableKeys={[
              'job_to_be_done', 'tension', 'benefit', 'brand_choice', 'format',
              'attributes', 'written_concept', 'project_name', 'market', 'opportunity_space',
            ]}
          />
          <GeneratorConfigPanel
            generatorKey="visual_concept_edit"
            title="Visual Concept Edit"
            enabled
            hidePrompt
            hideVariables
            defaultVariableKeys={[]}
          />
        </div>
      )}
    </div>
  );
};

export default AdminAiPromptsTab;
